import { Box, IconButton, Stack } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import { ConfirmationDialog, CustomDataGrid } from "@shared/components";
import { useSortModel } from "@shared/components/grid-components/hooks/useSortModel";
import { TableActions } from "@shared/utils/constants";
import { formatDateTime, getActionPermission } from "@shared/utils/functions";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetAllPartnerChannels } from "../services/useGetAllPartnerChannels";
import { AllowedSortFields } from "../utils/constants";
import {
  IPartnerChannelRow,
  PartnerChannelsFormValues,
  PartnerChannelsProps,
  SortField,
} from "../utils/types";
import { useDeletePartnerChannel } from "../services/useDeletePartnerChannel";
import PartnerChannelForm from "./partnerChannelForm";
import { useUpdatePartnerChannel } from "../services/useUpdatePartnerChannel";
import { FormikHelpers } from "formik";
import { enqueueSnackbar } from "notistack";
import { FormActions } from "@shared/utils/constant";
import { Can } from "@core/casl-provider";

const PartnerChannelsDataGrid = ({
  visibleColumns,
  setPaginationModel,
  paginationModel,
}: PartnerChannelsProps) => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("searchQuery") ?? "";
  const updatedSince = searchParams.get("updatedSince") ?? "";
  const { sortModel, handleSortModelChange, orderBy, order } = useSortModel<SortField>({
    allowedSortFields: AllowedSortFields,
  });

  const { data: partnerChannelsList, isLoading } = useGetAllPartnerChannels({
    data: {
      pageNumber: String(paginationModel.page + 1),
      pageSize: String(paginationModel.pageSize),
      includeInactive: true,
      // Map unified searchQuery to titleName for backend filtering compatibility
      searchQuery: searchQuery,
      orderBy: orderBy,
      order: order,
      updatedSince,
    },
  });

  const { mutate: deleteMutation } = useDeletePartnerChannel();
  const { mutateAsync: updateFormMutationAsync, isPending: isFormPending } =
    useUpdatePartnerChannel();

  const [actionState, setActionState] = useState<{
    action: "EDIT" | "DELETE" | "";
    payload: Partial<PartnerChannelsFormValues>;
  }>({
    action: "",
    payload: {},
  });

  const [allConfigurations, setAllConfigurations] =
    useState<IPartnerChannelRow[]>();
  const clearActionState = () => setActionState({ action: "", payload: {} });

  const totalRows: number =
    (partnerChannelsList?.pagination?.total as number) ??
    allConfigurations?.length ??
    0;
  const columns: GridColDef<IPartnerChannelRow>[] = [
    {
      field: "channelId",
      headerName: "Channel ID",
      flex: 0.2,
      minWidth: 120,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<IPartnerChannelRow, string | number>
      ) => <span>{params.value}</span>,
    },
    {
      field: "channelName",
      headerName: "Channel Name",
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (
        params: GridRenderCellParams<IPartnerChannelRow, string>
      ) => <span style={{ textTransform: "capitalize" }}>{params.value}</span>,
    },
    {
      field: "subChannel",
      headerName: "Sub Channel",
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      field: "mainChannel",
      headerName: "Main Channel",
      flex: 1,
      minWidth: 180,
      sortable: true,
    },
    {
      field: "ggsChannel",
      headerName: "GGS Channel",
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (
        params: GridRenderCellParams<IPartnerChannelRow, string>
      ) => <span>{params.value}</span>,
    },

    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      minWidth: 210,
      sortable: true,
      renderCell: (params: GridRenderCellParams<IPartnerChannelRow, string>) =>
        formatDateTime(params.value),
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      renderCell: (
        params: GridRenderCellParams<
          IPartnerChannelRow,
          unknown,
          unknown,
          GridTreeNodeWithRender
        >
      ) => {
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems={"center"}
            sx={{ height: "100%" }}
          >
            {TableActions.map((action, index) => {
              const actionPermission = getActionPermission(action);

              return (
                <Can
                  do={actionPermission}
                  on="PartnerChannel"
                  key={index}
                >
                  <IconButton
                    key={index}
                    title={action.label}
                    style={{
                      backgroundColor: "primary.main",
                    }}
                    onClick={() =>
                      setActionState({
                        action: action.key,
                        payload: {
                          channelId: Number(params.row.channelId),
                          channelName: params.row.channelName,
                          subChannel: params.row.subChannel,
                          mainChannel: params.row.mainChannel,
                          ggsChannel: params.row.ggsChannel,
                        },
                      })
                    }
                  >
                    {action.icon}
                  </IconButton>
                </Can>
              );
            })}
          </Stack>
        );
      },
    },
  ];

  const handleDeleteConfirm = async () => {
    deleteMutation({ id: String(actionState.payload.channelId) });
    clearActionState();
  };

  const handleEditSubmit = async (
    values: PartnerChannelsFormValues & { id?: string },
    helpers: FormikHelpers<PartnerChannelsFormValues & { id?: string }>
  ) => {
    try {
      const channelId = actionState.payload.channelId;
      const payloadData = { ...values };
      delete payloadData.channelId;

      await updateFormMutationAsync({
        payload: { channelId: channelId as number, payloadData },
      });
      clearActionState();
    } catch (error: unknown) {
      const errMsg: string =
        error instanceof Error
          ? error.message
          : "Failed to update configuration";
      enqueueSnackbar(errMsg, {
        variant: "error",
        autoHideDuration: 5000,
        preventDuplicate: true,
      });
    } finally {
      helpers.setSubmitting(false);
    }
  };

  useEffect(() => {
    if (partnerChannelsList?.data) {
      setAllConfigurations(partnerChannelsList.data);
    }
  }, [partnerChannelsList]);

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        overflow: "hidden",
      })}
    >
      <CustomDataGrid<IPartnerChannelRow>
        rows={allConfigurations}
        columns={columns}
        visibleColumns={visibleColumns}
        getRowId={(row) =>
          row.id ??
          row.channelId ??
          `${row.channelName}-${row.subChannel}-${row.mainChannel}-${
            row.updatedAt ?? ""
          }`
        }
        rowCount={totalRows}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        autoHeight
        disableSelectionOnClick
        loading={isLoading}
        dataGridSx={(theme) => ({
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        })}
      />

      <ConfirmationDialog
        open={actionState.action === FormActions.DELETE}
        onClose={clearActionState}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${actionState.payload.channelName ?? "Partner Channel"}`}
        message="Are you sure you want to Delete?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="primary"
      />
      <PartnerChannelForm
        open={actionState.action === FormActions.EDIT}
        onClose={clearActionState}
        title="Edit Partner Channel"
        initialValues={actionState.payload}
        disable={isFormPending}
        onSubmit={handleEditSubmit}
        showCloseButton
      />
    </Box>
  );
};

export default PartnerChannelsDataGrid;
